/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { catchError, combineLatest, of, Subscription } from 'rxjs';
import { TSortDirection } from 'src/app/shared/classes/grid-page-info';
import { GQLResponse } from 'src/app/shared/interfaces/graphql.interface';
import {
  IGroupEnrollment,
  IGroupEnrollmentPayer,
  IGroupEnrollmentPayersParams,
  IGroupEnrollmentStatus,
} from 'src/app/shared/interfaces/group-enrollment/group-enrollment.interface';
import {
  IGQLPayersParams,
  IPayer,
} from 'src/app/shared/interfaces/payer/payers.interface';
import { AlertService } from 'src/app/shared/services/alert.service';
import { GroupEnrollmentGQLService } from 'src/app/shared/services/group-enrollment/insurance/group-enrollment.graphql.service';
import { GroupEnrollmentService } from 'src/app/shared/services/group-enrollment/insurance/group-enrollment.service';
import { ModalService } from 'src/app/shared/services/modal.service';
import { PayerGQLService } from 'src/app/shared/services/payers/payers.graphql.service';
import { UserStateService } from 'src/app/shared/services/users/user-state.service';

@Component({
  selector: 'app-add-group-enrollments-modal',
  templateUrl: './add-group-enrollments-modal.component.html',
  styleUrls: ['./add-group-enrollments-modal.component.scss'],
})
export class AddGroupEnrollmentsModalComponent implements OnInit, OnDestroy {
  groupEnrollment: IGroupEnrollment;

  groupEnrollmentTypeCheckboxes: EnrollmentTypeCheckbox[] = [
    {
      controlName: 'requiresEnrollment',
      disabled: false,
      error: false,
      label: 'Professional',
      saved: false,
      value: 'PROFESSIONAL',
    },
    {
      controlName: 'institutionalEnrollment',
      disabled: false,
      error: false,
      label: 'Institutional',
      saved: false,
      value: 'INSTITUTIONAL',
    },
    {
      controlName: 'eraEnrollment',
      disabled: false,
      error: false,
      label: 'ERA',
      saved: false,
      value: 'ERA',
    },
    {
      controlName: 'claimStatusEnrollment',
      disabled: false,
      error: false,
      label: 'Claim Status',
      saved: false,
      value: 'CLAIMSTATUS',
    },
    {
      controlName: 'eligibilityEnrollment',
      disabled: false,
      error: false,
      label: 'Eligibility',
      saved: false,
      value: 'ELIGIBILITY',
    },
  ];

  selectedEnrollmentTypes: string[] = [];
  documents: string[];
  statuses: IGroupEnrollmentStatus[];

  groupEnrollmentForm: UntypedFormGroup;

  ///component core
  isLoading = true;
  isEditMode = false;
  isGroupEnrollmentPayer = false;

  npiList: string[];
  taxIdList: string[];

  /// Payer
  isPayerLoading = true;
  selectedPayer?: IGroupEnrollmentPayer;
  selectedGraphPayer?: IPayer;
  groupEnrollmentPayers: IPayer[] = [];

  //payerRequest
  payerCursor: string;
  hasNextPagePayer = false;
  payerParams: IGQLPayersParams;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private activeModal: NgbActiveModal,
    private fb: UntypedFormBuilder,
    private groupEnrollmentService: GroupEnrollmentService,
    private groupEnrollmentGQLService: GroupEnrollmentGQLService,
    private alertService: AlertService,
    private modalService: ModalService,
    private userStateService: UserStateService,
    private payerGQLService: PayerGQLService,
  ) {}

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  ngOnInit() {
    this.setGroupEnrollmentForm();
    this.handleFormValueChanges();
    this.togglePayersDisable();
    this.loadModalData();
  }

  close() {
    this.activeModal.dismiss();
  }

  onEnrollmentTypeCheck(event: any, enrollmentType: GroupEnrollmentType) {
    const fieldKey = this.getEnrollmentKeyByValue(enrollmentType);

    if (event.target.checked) {
      this.selectedEnrollmentTypes.push(fieldKey);
    } else {
      this.selectedEnrollmentTypes = this.selectedEnrollmentTypes.filter(
        (x) => {
          return x !== fieldKey;
        },
      );
    }

    if (!this.selectedPayer && !this.selectedGraphPayer) {
      this.clearPayerData();
      this.groupEnrollmentPayers = [];

      const values = this.selectedEnrollmentTypes.reduce<
        Record<string, { eq: string }>[]
      >((arr, current) => {
        arr.push({ [current]: { eq: 'Y' } });
        return arr;
      }, []);

      const params: IGQLPayersParams = {
        filter: {
          ...this.payerParams.filter,
        },
        ...this.payerParams,
      };

      if (values.length > 0 && params.filter) {
        params.filter.and = values;
      }

      this.payerParams = params;

      this.loadPayers(params);
    }
  }

  retry(formatedFormValue: IGroupEnrollment, enrollmentType: string, y): any {
    const req = this.groupEnrollmentService
      .createGroupEnrollment({
        ...formatedFormValue,
        enrollmentType,
      })
      .pipe(
        catchError((err) => {
          return of(err);
        }),
      );
    req.subscribe({
      next: (result) => {
        const value = result;
        if (value instanceof HttpErrorResponse) {
          if (!value.error.includes('json')) {
            this.alertService.error(
              value.error as HttpErrorResponse,
              undefined,
              true,
            );
          }
          return value.error;
        } else {
          const checkboxFormKey = y;
          const checkbox = this.groupEnrollmentTypeCheckboxes.find((x) => {
            return x.controlName === checkboxFormKey;
          });
          if (checkbox) {
            checkbox.disabled = true;
            checkbox.saved = true;
          }
          const successMessage = `Group Enrollment Type (${y}): ${value.seqNo} was created successfully!`;
          this.alertService.success(successMessage);
          return null;
        }
      },
    });
  }

  onSave() {
    this.isLoading = true;

    const formValue = this.groupEnrollmentForm.getRawValue();
    const formatedFormValue: IGroupEnrollment = {
      ...formValue,
      payerId: formValue.payerId ? formValue.payerId.toString() : '',
      enrollmentOwner: formValue.enrollmentOwner
        ? formValue.enrollmentOwner.toString()
        : '',
    };

    const requests$ = this.selectedEnrollmentTypes.reduce((obj, current) => {
      obj[current] = this.groupEnrollmentService
        .createGroupEnrollment({
          ...formatedFormValue,
          enrollmentType: groupEnrollmentType[current],
        })
        .pipe(
          catchError((err) => {
            return of(err);
          }),
        );
      return obj;
    }, {});

    this.subscriptions.add(
      combineLatest(requests$).subscribe({
        next: (results) => {
          let isErrors: boolean = false as boolean;

          Object.keys(results).forEach((y: any) => {
            const value = results[y] as any;
            const checkboxFormKey = y;
            const checkbox = this.groupEnrollmentTypeCheckboxes.find((x) => {
              return x.controlName === checkboxFormKey;
            });

            if (value instanceof HttpErrorResponse) {
              const errroList = value.error.errors;
              const errorText = errroList?.length
                ? errroList
                    .map((e) => {
                      return e;
                    })
                    .join('\r\n')
                : value.error;
              const errorMessage = `Error in saving Group Enrollment Type (${y}): ${errorText}`;

              if (errorMessage.includes("'json'")) {
                let res = this.retry(
                  formatedFormValue,
                  groupEnrollmentType[y] as string,
                  y,
                );
                let loopKiller = 0;
                while (res?.includes("'json'") || loopKiller > 10) {
                  res = this.retry(
                    formatedFormValue,
                    groupEnrollmentType[y] as string,
                    y,
                  );
                  loopKiller += 1;
                }
              } else {
                this.alertService.error(errorMessage, undefined, true);
                isErrors = true;
              }
            } else {
              if (checkbox) {
                checkbox.disabled = true;
                checkbox.saved = true;
              }

              const successMessage = `Group Enrollment Type (${y}): ${value.seqNo} was created successfully!`;
              this.alertService.success(successMessage);
            }
          });

          this.isLoading = false;
          if (!isErrors) {
            this.addMoreEnrollmentModal();
          }
        },
      }),
    );
  }

  onPayerSelect(payer?: IPayer) {
    if (payer) {
      for (const checkbox of this.groupEnrollmentTypeCheckboxes) {
        if (payer[checkbox.controlName] === 'N') {
          checkbox.disabled = true;
        }
      }
    }

    if (payer) {
      this.loadEnrollmentPayer(payer);
    } else {
      this.clearPayerData();
    }
  }

  onScrollToEnd() {
    if (this.hasNextPagePayer) {
      const params: IGQLPayersParams = {
        after: this.payerCursor,
        ...this.payerParams,
      };
      this.loadPayers(params);
    }
  }

  onSearch(data: { term: string; items: IGroupEnrollmentPayer[] }) {
    setTimeout(() => {
      if (data.term.length > 0 && data.items.length === 0) {
        const params = {
          filter: {
            ...this.payerParams.filter,
            legacy: 'N',
            or: {
              neicId: data.term,
              name: data.term,
            },
          },
        };
        //console.log(params);
        this.payerParams = params;

        this.loadPayers(params);
      }
    }, 750);
  }

  customSearchFn(term: string, item: IPayer) {
    term = term.toLocaleLowerCase();
    return (
      item.neicId.toLocaleLowerCase().includes(term) ||
      item.name.toLocaleLowerCase().includes(term)
    );
  }

  onPayerClear() {
    for (const checkbox of this.groupEnrollmentTypeCheckboxes) {
      if (!checkbox.saved) {
        checkbox.disabled = false;
      }
    }
  }
  private loadEnrollmentPayer(payer: IPayer) {
    this.isLoading = true;

    const params: IGroupEnrollmentPayersParams = {
      ScId: payer.neicId,
    };

    this.subscriptions.add(
      this.groupEnrollmentService.getGroupEnrollmentPayers(params).subscribe({
        next: (payers) => {
          this.isLoading = false;

          //review
          const groupEnrollmentPayer =
            payers.length > 0
              ? payers.find((p) => {
                  return (
                    p.processorId.trim().toLowerCase() ===
                    payer.processorId.trim().toLowerCase()
                  );
                })
              : payer;
          this.setPayer(groupEnrollmentPayer);

          // this.togglePayersDisable();
        },
        error: (error: unknown) => {
          this.isPayerLoading = false;
          this.togglePayersDisable();
          if (error) {
            this.alertService.error(error as HttpErrorResponse | string);
          }
        },
      }),
    );
  }

  private loadPayers(params?: IGQLPayersParams) {
    this.isPayerLoading = true;
    this.togglePayersDisable();

    const gqlParams = {
      ...params,
      filter: {
        ...params?.filter,
        recordStatus: 'A',
      },
      orderby: 'neicId',
      direction: 'ASC' as TSortDirection,
    };

    this.subscriptions.add(
      this.payerGQLService.getPayers(gqlParams).subscribe({
        next: (response) => {
          this.isPayerLoading = false;

          const payers = response.nodes;
          this.hasNextPagePayer = response.pageInfo.hasNextPage;

          this.groupEnrollmentPayers = [
            ...this.groupEnrollmentPayers,
            ...payers,
          ];

          this.togglePayersDisable();
        },
        error: (error: unknown) => {
          this.isPayerLoading = false;
          this.togglePayersDisable();
          if (error) {
            this.alertService.error(error as HttpErrorResponse | string);
          }
        },
      }),
    );
  }

  download(file) {
    try {
      const fileName = file.item as string;
      const checkFileType = fileName.split('.').pop();
      let fileType = 'unknown';
      if (checkFileType == '.txt') {
        fileType = 'text/plain';
      }
      if (checkFileType == '.pdf') {
        fileType = 'application/pdf';
      }
      if (checkFileType == '.doc') {
        fileType = 'application/vnd.ms-word';
      }
      if (checkFileType == '.docx') {
        fileType = 'application/vnd.ms-word';
      }
      if (checkFileType == '.xls') {
        fileType = 'application/vnd.ms-excel';
      }
      if (checkFileType == '.png') {
        fileType = 'image/png';
      }
      if (checkFileType == '.jpg') {
        fileType = 'image/jpeg';
      }
      if (checkFileType == '.jpeg') {
        fileType = 'image/jpeg';
      }
      if (checkFileType == '.gif') {
        fileType = 'image/gif';
      }
      if (checkFileType == '.csv') {
        fileType = 'text/csv';
      }
      this.groupEnrollmentService
        .DownloadFile(fileName, fileType)
        .subscribe((file) => {
          if (file.body) {
            const url = window.URL.createObjectURL(file.body);
            window.open(url);
          }
        });
    } catch {
      this.isLoading = false;
      this.alertService.error('Something went wrong. Please try again.');
    }
  }

  private setPayer(payer?: IGroupEnrollmentPayer | IPayer) {
    this.isGroupEnrollmentPayer = !!(payer as IGroupEnrollmentPayer).scId;

    if (this.isGroupEnrollmentPayer) {
      this.selectedPayer = payer as IGroupEnrollmentPayer;

      this.documents = [
        this.selectedPayer.doc1,
        this.selectedPayer.doc2,
        this.selectedPayer.doc3,
        this.selectedPayer.doc4,
        this.selectedPayer.doc5,
      ].filter(Boolean);

      this.groupEnrollmentForm.patchValue({
        payerId: this.selectedPayer.scId,
        notes: this.selectedPayer.notes,
      });
    } else {
      this.selectedGraphPayer = payer as IPayer;

      this.groupEnrollmentForm.patchValue({
        payerId: this.selectedGraphPayer.neicId,
        notes: this.selectedGraphPayer.notes,
      });
    }

    const processorId = this.groupEnrollmentForm.get('processorId');

    if (!processorId?.value) {
      processorId?.enable();
    }
  }

  private loadModalData() {
    this.payerParams = {
      filter: {
        recordStatus: 'A',
        legacy: 'N',
      },
      orderby: 'neicId',
      direction: 'ASC' as TSortDirection,
    };

    const requests$ = [
      this.groupEnrollmentGQLService.getGroupEnrollmentStatuses(),
      this.groupEnrollmentGQLService.getGroupEnrollmentNPIList(
        this.groupEnrollment.id,
      ),
      this.groupEnrollmentGQLService.getGroupEnrollmentTaxIdList(
        this.groupEnrollment.id,
      ),
      this.payerGQLService.getPayers(this.payerParams),
    ];

    this.subscriptions.add(
      combineLatest(requests$).subscribe({
        next: ([
          groupEnrollmentStatuses,
          groupEnrollmentNPIList,
          groupEnrollmentTaxIdList,
          groupEnrollmentPayersResponse,
        ]: [
          IGroupEnrollmentStatus[],
          string[],
          string[],
          GQLResponse<IPayer>,
        ]) => {
          this.isLoading = false;
          this.isPayerLoading = false;

          this.statuses = groupEnrollmentStatuses;
          this.groupEnrollmentPayers = groupEnrollmentPayersResponse.nodes;
          this.npiList = groupEnrollmentNPIList;
          this.taxIdList = groupEnrollmentTaxIdList;

          this.hasNextPagePayer =
            groupEnrollmentPayersResponse.pageInfo.hasNextPage;
          this.payerCursor = groupEnrollmentPayersResponse.pageInfo.endCursor;

          this.togglePayersDisable();
        },
        error: () => {
          this.isLoading = false;
          this.isPayerLoading = false;
          this.alertService.error('Something went wrong. Please try again.');
        },
      }),
    );
  }

  private clearPayerData() {
    this.selectedPayer = undefined;
    this.selectedGraphPayer = undefined;
    this.documents = [];

    this.groupEnrollmentForm.patchValue({
      payerId: null,
      notes: '',
      enrollmentType: '',
    });
  }

  private togglePayersDisable() {
    const control = this.groupEnrollmentForm.controls.payerId;

    if (control.disabled) {
      control.enable();
    } else {
      control.disable();
    }
  }

  private addMoreEnrollmentModal() {
    const modalText = {
      bodyTitle:
        'Group Enrollment saved. Do you want to add another Enrollment?',
      modalTitle: 'Group Enrollment saved.',
      cancelButtonText: 'No',
      confirmButtonText: 'Yes',
    };

    this.modalService
      .open('confirm', modalText)
      .result.then(() => {
        this.groupEnrollmentForm.reset();
        this.clearPayerData();
        return;
      })
      .catch(() => {
        this.activeModal.close();
      });
  }

  private handleFormValueChanges() {
    const payerIdControl = this.groupEnrollmentForm.get('payerId');

    this.subscriptions.add(
      payerIdControl?.valueChanges.subscribe((id) => {
        const payer = id
          ? this.groupEnrollmentPayers.find((x) => {
              return x.neicId == id;
            })
          : null;

        this.groupEnrollmentForm.patchValue({
          // processorId: payer ? payer.processorId : this.groupEnrollment?.processorId
          processorId: payer ? payer.processorId : '',
        });
      }),
    );
  }

  private getEnrollmentKeyByValue(
    value: GroupEnrollmentType,
  ): GroupEnrollmentKey {
    return Object.keys(groupEnrollmentType).find((key) => {
      return groupEnrollmentType[key] === value;
    }) as GroupEnrollmentKey;
  }

  private setGroupEnrollmentForm() {
    this.groupEnrollmentForm = this.fb.group({
      id: [
        this.groupEnrollment.id || '',
        [Validators.maxLength(6), Validators.required],
      ],
      payerId: [null, Validators.required],
      // enrollmentType:   ['', [Validators.maxLength(20), Validators.required]],
      npi: ['', [Validators.maxLength(10), Validators.required]],
      taxId: ['', [Validators.maxLength(9), Validators.required]],
      // this.groupEnrollment.processorId
      processorId: [
        { value: '', disabled: false },
        [Validators.maxLength(12), Validators.required],
      ],
      enrollmentStatus: ['', [Validators.maxLength(1), Validators.required]],
      followUpDate: [{ value: null, disabled: false }],
      actionNotes: [
        { value: '', disabled: false },
        [Validators.maxLength(16_000)],
      ],
      message: [{ value: '', disabled: false }, [Validators.maxLength(1500)]],
      // this.groupEnrollment.caseNumber
      caseNumber: [{ value: '', disabled: false }, [Validators.maxLength(50)]],
      enrollmentOwner: [this.userStateService.user?.id ?? ''],
    });
  }
}

const groupEnrollmentType = {
  requiresEnrollment: 'PROFESSIONAL',
  institutionalEnrollment: 'INSTITUTIONAL',
  eraEnrollment: 'ERA',
  claimStatusEnrollment: 'CLAIMSTATUS',
  eligibilityEnrollment: 'ELIGIBILITY',
} as const;

type TypeGroupEnrollmentType = typeof groupEnrollmentType;
export type GroupEnrollmentKey = keyof TypeGroupEnrollmentType;
export type GroupEnrollmentType =
  TypeGroupEnrollmentType[keyof TypeGroupEnrollmentType];

type EnrollmentTypeCheckbox = {
  controlName: GroupEnrollmentKey;
  disabled: boolean;
  error: boolean;
  label: string;
  saved: boolean;
  value: GroupEnrollmentType;
};
